import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Percent, Save, RotateCcw, Lock } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export const TaxAssumptions = () => {
  const [inflation, setInflation] = useState(2.5);
  const [brackets, setBrackets] = useState([]);
  const [defaults, setDefaults] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [taxRes, adminRes] = await Promise.all([
        api.get("/settings/tax"),
        api.get("/admin/check").catch(() => ({ data: { is_admin: false } })),
      ]);
      setInflation(Number((taxRes.data.inflation_rate * 100).toFixed(2)));
      setBrackets(taxRes.data.brackets.map((b) => ({ ...b })));
      setDefaults(taxRes.data.defaults);
      setIsAdmin(adminRes.data.is_admin);
    } catch (e) {
      console.error("Failed to load tax settings", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const updateBracket = (idx, field, value) => {
    setBrackets((prev) => prev.map((b, i) => (i === idx ? { ...b, [field]: value } : b)));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        inflation_rate: Number(inflation) / 100,
        brackets: brackets.map((b) => ({
          min: Number(b.min),
          max: b.max === null || b.max === "" ? null : Number(b.max),
          rate: Number(b.rate),
          label: b.label || `${Math.round(Number(b.rate) * 100)}% bracket`,
        })),
      };
      await api.put("/settings/tax", payload);
      toast.success("Tax assumptions updated. Estimates will use these figures.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save tax assumptions");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (!defaults) return;
    setInflation(Number((defaults.inflation_rate * 100).toFixed(2)));
    setBrackets(defaults.brackets.map((b) => ({ ...b })));
  };

  if (loading) return null;

  return (
    <Card data-testid="tax-assumptions-card">
      <CardHeader>
        <CardTitle className="font-heading flex items-center gap-2">
          <Percent className="w-5 h-5" />
          Tax Assumptions
        </CardTitle>
        <CardDescription>
          Figures used across CGT and negative-gearing estimates. Update these to match the latest ATO
          figures. General estimates only — not tax advice.
          {!isAdmin && (
            <span className="flex items-center gap-1 text-amber-600 mt-2 text-xs">
              <Lock className="w-3 h-3" /> Only an administrator can change these values.
            </span>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="space-y-2 max-w-xs">
          <Label htmlFor="inflation">Assumed annual inflation (for CGT indexation)</Label>
          <div className="relative">
            <Input
              id="inflation"
              type="number"
              step="0.1"
              value={inflation}
              disabled={!isAdmin}
              onChange={(e) => setInflation(e.target.value)}
              data-testid="tax-inflation-input"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">%</span>
          </div>
        </div>

        <div>
          <Label className="mb-2 block">Resident marginal tax brackets</Label>
          <div className="space-y-2 overflow-x-auto" data-testid="tax-brackets">
            <div className="grid grid-cols-12 gap-2 text-xs text-slate-500 font-medium px-1 min-w-[420px]">
              <span className="col-span-4">From ($)</span>
              <span className="col-span-4">To ($, blank = no cap)</span>
              <span className="col-span-4">Rate (%)</span>
            </div>
            {brackets.map((b, idx) => (
              <div className="grid grid-cols-12 gap-2 items-center min-w-[420px]" key={idx} data-testid={`tax-bracket-${idx}`}>
                <Input
                  className="col-span-4"
                  type="number"
                  value={b.min}
                  disabled={!isAdmin}
                  onChange={(e) => updateBracket(idx, "min", e.target.value)}
                />
                <Input
                  className="col-span-4"
                  type="number"
                  value={b.max === null ? "" : b.max}
                  placeholder="—"
                  disabled={!isAdmin}
                  onChange={(e) => updateBracket(idx, "max", e.target.value === "" ? null : e.target.value)}
                />
                <Input
                  className="col-span-4"
                  type="number"
                  step="0.01"
                  value={b.rate}
                  disabled={!isAdmin}
                  onChange={(e) => updateBracket(idx, "rate", e.target.value)}
                  title="Enter as a decimal, e.g. 0.30 for 30%"
                />
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-400 mt-2">Rates are decimals (0.30 = 30%).</p>
        </div>

        {isAdmin && (
          <div className="flex gap-2 pt-2 border-t border-slate-100">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#547d91] hover:bg-[#456a7a]"
              data-testid="save-tax-assumptions-btn"
            >
              <Save className="w-4 h-4 mr-1" /> {saving ? "Saving..." : "Save assumptions"}
            </Button>
            <Button variant="outline" onClick={handleReset} data-testid="reset-tax-assumptions-btn">
              <RotateCcw className="w-4 h-4 mr-1" /> Reset to defaults
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
